/**
 * Attach one document to a candidate email (case material, mostly).
 *
 * The file is read into memory as base64 and handed to the proxy with the send;
 * nothing is stored. That keeps the ad-hoc path — "this candidate gets the case
 * study with their invite" — free of any upload, retention or cleanup question.
 * The per-role material that IS stored is configured on the panel instead.
 */
import { useRef, useState } from 'react';
import { Button } from '@finn-auto/design-system/atoms/button';

/** Mirrors the proxy's allowlist (lib/taOutboundMail.js) — keep the two in step. */
const ACCEPT = [
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.ms-powerpoint',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'text/plain',
  'image/png',
  'image/jpeg',
].join(',');

/** Mirrors the proxy's cap (lib/taOutboundMail.js) — memory-bound, not Gmail-bound. */
export const MAX_ATTACHMENT_BYTES = 3 * 1024 * 1024;

export interface OutboundAttachment {
  filename: string;
  mimeType: string;
  contentBase64: string;
  bytes: number;
}

/** FileReader gives a data: URL; the payload is everything after the comma. */
function readAsBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error('Could not read the file'));
    reader.onload = () => resolve(String(reader.result || '').split(',')[1] || '');
    reader.readAsDataURL(file);
  });
}

export function AttachmentPicker({
  attachment,
  onChange,
  disabled,
  label = 'Case material (optional)',
}: {
  attachment: OutboundAttachment | null;
  onChange: (next: OutboundAttachment | null) => void;
  disabled?: boolean;
  label?: string;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [error, setError] = useState<string | null>(null);

  const pick = async (file: File | undefined) => {
    if (!file) return;
    setError(null);
    if (file.size > MAX_ATTACHMENT_BYTES) {
      setError(`That file is ${(file.size / 1024 / 1024).toFixed(1)} MB — the limit is 3 MB.`);
      return;
    }
    try {
      const contentBase64 = await readAsBase64(file);
      onChange({
        filename: file.name,
        mimeType: file.type || 'application/octet-stream',
        contentBase64,
        bytes: file.size,
      });
    } catch {
      setError('Could not read that file.');
    }
  };

  return (
    <div className="space-y-1.5">
      <span className="block text-sm font-medium text-gray-700">{label}</span>
      {attachment ? (
        <div className="flex flex-wrap items-center gap-2 rounded-lg bg-gray-50 px-3 py-2 text-sm text-gray-700">
          <span>{attachment.filename}</span>
          <span className="text-xs text-gray-400">{(attachment.bytes / 1024 / 1024).toFixed(1)} MB</span>
          <Button
            size="sm"
            variant="ghost"
            className="ml-auto"
            disabled={disabled}
            onClick={() => {
              onChange(null);
              if (inputRef.current) inputRef.current.value = '';
            }}
          >
            Remove
          </Button>
        </div>
      ) : (
        <Button size="sm" variant="outline" disabled={disabled} onClick={() => inputRef.current?.click()}>
          Attach a file
        </Button>
      )}
      <input
        ref={inputRef}
        type="file"
        accept={ACCEPT}
        className="hidden"
        onChange={(e) => void pick(e.target.files?.[0])}
      />
      {error && <p className="text-xs text-[#791F1F]">{error}</p>}
    </div>
  );
}
